import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, MapPin, Sparkles, ArrowUpRight, Compass, Gift, HandHeart, TrendingUp, Users, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, isCityPriority, buildPageMeta, slugifyTaxonomy } from "@/lib/seoIndex";
import OfferCard from "@/components/OfferCard";
import {
  getCityEditorial,
  CITY_FAQ,
  WAYS_TO_HELP,
  PRIORITY_CITIES,
} from "@/data/cityEditorial";

const CATEGORY_ICON = {
  donation: Gift,
  volunteering: HandHeart,
  pro_bono_service: Sparkles,
  impact_investment: TrendingUp,
  fundraising: Heart,
};

const SUPPORTED_LANGS = ["en", "de", "fr", "es"];

export default function CityLandingPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* eslint-disable */
  useEffect(() => {
    setLoading(true);
    setError(false);
    api
      .get(`/cities/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);
  /* eslint-enable */

  // Build SEO data — even before payload arrives we set a sensible title
  const baseUrl = buildCanonical(`/c/${slug}`);
  const cityName = data?.city || (slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");
  const countryName = data?.country || "";
  const offerCount = data?.total_offers || 0;

  const localizedCity = data ? t(`cities.${data.city}`, { defaultValue: data.city }) : cityName;
  const localizedCountry = data ? t(`countries.${data.country}`, { defaultValue: data.country }) : countryName;

  const cityMeta = buildPageMeta("city", {
    cityName: localizedCity,
    countryName: localizedCountry,
    totalOffers: offerCount,
  });

  const isPriority = isCityPriority(slug);
  const editorial = isPriority ? getCityEditorial(slug) : null;

  // Localised FAQs are interpolated client-side so each city renders unique
  // crawlable Q/A text (and a unique FAQPage JSON-LD payload).
  const localizedFaqs = isPriority
    ? CITY_FAQ.map((f) => ({
        q: f.q.replace(/\{\{city\}\}/g, localizedCity || cityName),
        a: f.a.replace(/\{\{city\}\}/g, localizedCity || cityName),
      }))
    : [];

  const otherPriorityCities = isPriority
    ? PRIORITY_CITIES.filter((c) => c.slug !== String(slug || "").toLowerCase())
    : [];

  // Build a combined JSON-LD graph: ItemList of top offers + FAQPage when
  // the priority editorial FAQs are rendered. Stays a single <script> tag.
  const jsonLdGraph = data
    ? (() => {
        const graph = [
          {
            "@type": "ItemList",
            name: `Pro bono opportunities in ${data.city}`,
            numberOfItems: data.total_offers,
            itemListElement: (data.top_offers || []).map((o, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Organization",
                name: o.organization,
                url: o.url,
                description: (o.description || "").slice(0, 200),
              },
            })),
          },
        ];
        if (localizedFaqs.length > 0) {
          graph.push({
            "@type": "FAQPage",
            mainEntity: localizedFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          });
        }
        return { "@context": "https://schema.org", "@graph": graph };
      })()
    : undefined;

  useSEO({
    robots: decideRobots({
      pageType: "city",
      totalOffers: offerCount,
      isPriority,
    }),
    title: cityMeta.title,
    description: cityMeta.description,
    canonical: baseUrl,
    alternates: SUPPORTED_LANGS.map((l) => ({
      hreflang: l,
      href: `${baseUrl}?lang=${l}`,
    })).concat([{ hreflang: "x-default", href: baseUrl }]),
    jsonLd: jsonLdGraph,
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="h-12 w-2/3 bg-[#EFEAE0] rounded animate-pulse" />
        <div className="mt-6 h-4 w-1/2 bg-[#EFEAE0] rounded animate-pulse" />
        <div className="mt-12 grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card-clay overflow-hidden">
              <div className="aspect-[4/3] bg-[#EFEAE0] animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-4 w-24 bg-[#EFEAE0] rounded animate-pulse" />
                <div className="h-5 bg-[#EFEAE0] rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="city-not-found">
        <p className="text-3xl mb-2">🌍</p>
        <h1 className="text-3xl font-black">{t("city.not_found_title", { defaultValue: "City not found" })}</h1>
        <p className="mt-3 text-[#5F6B61]">
          {t("city.not_found_body", {
            defaultValue: "We don't cover this city yet. Browse all 24 European cities we currently aggregate.",
          })}
        </p>
        <Link to="/offers" className="btn-primary mt-6 inline-flex" data-testid="cta-browse-all">
          {t("city.browse_all", { defaultValue: "Browse all offers" })}
        </Link>
      </div>
    );
  }

  const categoryEntries = Object.entries(data.by_category || {}).sort(([, a], [, b]) => b - a);
  const causeEntries = Object.entries(data.by_cause || {}).sort(([, a], [, b]) => b - a);

  return (
    <div data-testid={`city-page-${slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#F5F1E6] via-[#EFEAE0] to-[#E8EDDF]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <p className="eyebrow flex items-center gap-2" data-testid="city-breadcrumb">
            <Link to="/" className="hover:text-[#5B8C66]">{t("nav.home", { defaultValue: "Home" })}</Link>
            <span>·</span>
            <Link to="/offers" className="hover:text-[#5B8C66]">{t("nav.explore", { defaultValue: "Explore" })}</Link>
            <span>·</span>
            <span>{localizedCountry}</span>
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="city-h1">
            {t("city.h1", {
              defaultValue: "Do good in {{city}}",
              city: localizedCity,
            })}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#3A463C] max-w-2xl">
            {t("city.hero_intro", {
              defaultValue:
                "{{count}} active pro bono opportunities with local organisations in {{city}}, {{country}} — donate, volunteer or share your skills.",
              count: offerCount,
              city: localizedCity,
              country: localizedCountry,
            })}
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-3" data-testid="city-stats">
            <span className="px-4 py-2 rounded-full bg-white border border-[#DED7C7] text-sm">
              <strong className="text-[#5B8C66] font-black">{offerCount}</strong> {t("city.stat_offers", { defaultValue: "offers" })}
            </span>
            <span className="px-4 py-2 rounded-full bg-white border border-[#DED7C7] text-sm">
              <strong className="text-[#5B8C66] font-black">{data.organizations}</strong> {t("city.stat_orgs", { defaultValue: "organisations" })}
            </span>
            <span className="px-4 py-2 rounded-full bg-white border border-[#DED7C7] text-sm">
              <strong className="text-[#5B8C66] font-black">{categoryEntries.length}</strong> {t("city.stat_categories", { defaultValue: "ways to help" })}
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/offers?city=${encodeURIComponent(data.city)}`} className="btn-primary" data-testid="cta-see-all">
              {t("city.cta_see_all", { defaultValue: "See all {{city}} offers", city: localizedCity })}
              <ArrowUpRight size={16} className="ml-1.5" strokeWidth={2} />
            </Link>
            <Link to="/list-offer" className="btn-pill border border-[#1F2A24] px-5 py-3 text-sm font-semibold inline-flex items-center bg-white hover:bg-[#F5F1E6]" data-testid="cta-submit-offer">
              {t("city.cta_submit", { defaultValue: "Submit your own" })}
            </Link>
          </div>
        </div>
      </section>

      {/* Top offers — placed immediately after the hero so visitors see
          real opportunities first, in line with the City×Cause page. */}
      {data.top_offers?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="eyebrow">{t("city.section_top_eyebrow", { defaultValue: "Featured" })}</p>
              <h2 className="mt-2 text-2xl md:text-3xl font-black">
                {t("city.section_top_title", { defaultValue: "Top opportunities in {{city}}", city: localizedCity })}
              </h2>
            </div>
            <Link to={`/offers?city=${encodeURIComponent(data.city)}`} className="text-sm font-bold text-[#5B8C66] inline-flex items-center gap-1" data-testid="see-all-link">
              {t("city.see_all_link", { defaultValue: "See all {{count}}", count: offerCount })}
              <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="city-top-offers">
            {data.top_offers.map((o) => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </div>
        </section>
      )}

      {/* Ways to help — priority cities only. Maps to existing /offers filters
          so each card lands the user on a useful filtered list. */}
      {isPriority && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="city-ways-to-help">
          <p className="eyebrow">
            {t("city.section_ways_eyebrow", { defaultValue: "Ways to help" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_ways_title", {
              defaultValue: "Four practical ways to support {{city}}",
              city: localizedCity,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {WAYS_TO_HELP.map((w) => {
              const Icon = CATEGORY_ICON[w.category] || Sparkles;
              const inCat = (data.by_category || {})[w.category] || 0;
              return (
                <Link
                  key={w.key}
                  to={`/offers?city=${encodeURIComponent(data.city)}&category=${w.category}`}
                  className="card-clay p-5 hover:shadow-md transition group block"
                  data-testid={`way-${w.key}-link`}
                >
                  <Icon size={20} strokeWidth={1.75} className="text-[#5B8C66]" />
                  <p className="mt-3 text-sm font-bold leading-snug">
                    {t(`city.ways.${w.key}.label`, { defaultValue: w.label })}
                  </p>
                  <p className="mt-1.5 text-xs text-[#5F6B61] leading-relaxed">
                    {t(`city.ways.${w.key}.body`, { defaultValue: w.body })}
                  </p>
                  {inCat > 0 && (
                    <p className="mt-3 text-xs text-[#5B8C66] font-semibold inline-flex items-center gap-1">
                      {inCat} {inCat === 1
                        ? t("city.offer_singular", { defaultValue: "offer" })
                        : t("city.offer_plural", { defaultValue: "offers" })}
                      <ArrowUpRight size={12} strokeWidth={2.5} />
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Category breakdown */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
        <p className="eyebrow">{t("city.section_categories_eyebrow", { defaultValue: "How to help in {{city}}", city: localizedCity })}</p>
        <h2 className="mt-2 text-2xl md:text-3xl font-black">
          {t("city.section_categories_title", { defaultValue: "Pick your path" })}
        </h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="category-grid">
          {categoryEntries.map(([cat, n]) => {
            const Icon = CATEGORY_ICON[cat] || Sparkles;
            return (
              <Link
                key={cat}
                to={`/offers?city=${encodeURIComponent(data.city)}&category=${cat}`}
                className="card-clay p-5 hover:shadow-md transition group"
                data-testid={`category-${cat}-link`}
              >
                <Icon size={20} strokeWidth={1.75} className="text-[#5B8C66]" />
                <p className="mt-3 text-sm font-bold leading-snug">
                  {t(`categories.${cat}`, { defaultValue: cat.replace(/_/g, " ") })}
                </p>
                <p className="mt-1 text-xs text-[#5F6B61]">
                  {n} {n === 1 ? t("city.offer_singular", { defaultValue: "offer" }) : t("city.offer_plural", { defaultValue: "offers" })}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* About / editorial intro — priority cities only. 250-400 words of
          useful, city-specific context. Placed AFTER opportunities so long
          explanatory text never pushes real offers below the fold. Hidden
          gracefully for non-priority slugs. */}
      {editorial?.intro?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="city-editorial-intro">
          <p className="eyebrow">
            {t("city.section_intro_eyebrow", { defaultValue: "About {{city}}", city: localizedCity })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_intro_title", {
              defaultValue: "How {{city}} helps — and how you can take part",
              city: localizedCity,
            })}
          </h2>
          <div className="prose mt-6 max-w-none text-[#3A463C] leading-relaxed space-y-4 text-base md:text-lg">
            {editorial.intro.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </section>
      )}

      {/* Causes — descriptive City×Cause combo links. Only causes with
          at least 3 active offers in this city get promoted to a real
          /c/{city}/{cause} discovery page so we never surface empty pages. */}
      {causeEntries.filter(([, n]) => n >= 3).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12">
          <p className="eyebrow">{t("city.section_causes_eyebrow", { defaultValue: "Causes" })}</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_causes_title", {
              defaultValue: "What {{city}}-based NGOs work on",
              city: localizedCity,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="cause-combo-grid">
            {causeEntries
              .filter(([, n]) => n >= 3)
              .map(([cause, n]) => {
                const causeSlug = slugifyTaxonomy(cause);
                const causeLabel = t(`causes.${cause}`, { defaultValue: cause });
                return (
                  <Link
                    key={cause}
                    to={`/c/${slug}/${causeSlug}`}
                    className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between gap-3"
                    data-testid={`cause-combo-${causeSlug}`}
                  >
                    <div className="min-w-0">
                      <p className="font-bold leading-snug">
                        {t("city.cause_combo_anchor", {
                          defaultValue: "{{cause}} opportunities in {{city}}",
                          cause: causeLabel,
                          city: localizedCity,
                        })}
                      </p>
                      <p className="mt-1 text-xs text-[#5F6B61]">
                        {n} {n === 1
                          ? t("city.offer_singular", { defaultValue: "offer" })
                          : t("city.offer_plural", { defaultValue: "offers" })}
                      </p>
                    </div>
                    <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66] shrink-0" />
                  </Link>
                );
              })}
          </div>
        </section>
      )}

      {/* Sibling cities */}
      {data.sibling_cities?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 mb-8">
          <p className="eyebrow flex items-center gap-1.5">
            <Compass size={12} strokeWidth={2.5} />
            {t("city.section_siblings_eyebrow", { defaultValue: "Nearby" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_siblings_title", {
              defaultValue: "Other cities in {{country}}",
              country: localizedCountry,
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="sibling-cities">
            {data.sibling_cities.map((s) => (
              <Link
                key={s.slug}
                to={`/c/${s.slug}`}
                className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
                data-testid={`sibling-${s.slug}`}
              >
                <div>
                  <p className="font-bold">{t(`cities.${s.city}`, { defaultValue: s.city })}</p>
                  <p className="text-xs text-[#5F6B61]">
                    {s.count} {s.count === 1 ? t("city.offer_singular", { defaultValue: "offer" }) : t("city.offer_plural", { defaultValue: "offers" })}
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Other priority cities — cross-link grid, priority cities only.
          Helps Search engines understand the hub structure. */}
      {isPriority && otherPriorityCities.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="city-other-priority">
          <p className="eyebrow flex items-center gap-1.5">
            <Compass size={12} strokeWidth={2.5} />
            {t("city.section_other_priority_eyebrow", { defaultValue: "Explore other cities" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_other_priority_title", {
              defaultValue: "Other priority cities we actively cover",
            })}
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {otherPriorityCities.map((c) => (
              <Link
                key={c.slug}
                to={`/c/${c.slug}`}
                className="card-clay px-5 py-4 hover:shadow-md transition flex items-center justify-between"
                data-testid={`other-priority-${c.slug}`}
              >
                <div>
                  <p className="font-bold">{t(`cities.${c.name}`, { defaultValue: c.name })}</p>
                  <p className="text-xs text-[#5F6B61]">
                    {t(`countries.${c.country}`, { defaultValue: c.country })}
                  </p>
                </div>
                <ArrowUpRight size={16} strokeWidth={2} className="text-[#5B8C66]" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* City-specific FAQs — priority cities only. The same 6 questions are
          rendered with the city name interpolated so each page has unique
          crawlable Q/A text; mirrored in the FAQPage JSON-LD payload above. */}
      {isPriority && localizedFaqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="city-faqs">
          <p className="eyebrow flex items-center gap-1.5">
            <HelpCircle size={12} strokeWidth={2.5} />
            {t("city.section_faq_eyebrow", { defaultValue: "FAQ" })}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-black">
            {t("city.section_faq_title", {
              defaultValue: "Common questions about helping in {{city}}",
              city: localizedCity,
            })}
          </h2>
          <div className="mt-6 divide-y divide-[#E5DECC] border-y border-[#E5DECC]">
            {localizedFaqs.map((f, i) => (
              <details
                key={i}
                className="group py-5"
                data-testid={`city-faq-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-bold text-[#1F2A24] leading-snug">{f.q}</span>
                  <span className="mt-1 text-[#5B8C66] group-open:rotate-45 transition-transform select-none">+</span>
                </summary>
                <p className="mt-3 text-[#3A463C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Small factual helpfulness block — data-backed, neutral. */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="city-trust-block">
        <div className="border-t border-[#E5DECC] pt-6 text-sm text-[#5F6B61] leading-relaxed">
          <p>
            {t("city.trust_intro", {
              defaultValue:
                "Browse active opportunities from organisations working in {{city}}. Listings link to each organisation's own page, where you can check current details and next steps.",
              city: localizedCity,
            })}
          </p>
          <p className="mt-2">
            {t("city.trust_disclaimer", {
              defaultValue:
                "Availability, eligibility and next steps may vary by organisation. Updated from active listings.",
            })}
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16">
        <div className="card-clay p-10 md:p-14 text-center bg-gradient-to-br from-[#5B8C66] to-[#3F6147] text-white">
          <Users size={28} strokeWidth={1.5} className="mx-auto opacity-80" />
          <h2 className="mt-4 text-3xl md:text-4xl font-black">
            {t("city.final_cta_title", {
              defaultValue: "Run an NGO or programme in {{city}}?",
              city: localizedCity,
            })}
          </h2>
          <p className="mt-3 text-white/80 max-w-xl mx-auto">
            {t("city.final_cta_body", {
              defaultValue:
                "List your offer for free — typically published within 24 hours and seen by Europeans looking to do good locally.",
            })}
          </p>
          <Link
            to="/list-offer"
            className="btn-pill bg-white text-[#1F2A24] px-6 py-3 text-sm font-bold inline-flex items-center mt-6 hover:bg-[#F5F1E6]"
            data-testid="final-cta-list-offer"
          >
            {t("city.final_cta_button", { defaultValue: "List your offer" })}
          </Link>
        </div>
      </section>
    </div>
  );
}
